import React, { useEffect, useMemo, useRef, useState } from "react";
import api from '../api';

// ====== CONFIG ======
const FILES_BASE = "http://localhost:5000/api/files";

// Util: human readable size
const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Icon util
const ItemIcon = ({ type, ext }) => {
  if (type === "folder")
    return <i className="bi bi-folder-fill me-2" aria-hidden />;
  if (ext && [".jpg", ".jpeg", ".png", ".gif", ".webp"].includes(ext))
    return <i className="bi bi-image-fill me-2" aria-hidden />;
  if (ext && [".pdf"].includes(ext))
    return <i className="bi bi-file-earmark-pdf-fill me-2" aria-hidden />;
  if (ext && [".doc", ".docx"].includes(ext))
    return <i className="bi bi-file-earmark-word-fill me-2" aria-hidden />;
  if (ext && [".xls", ".xlsx"].includes(ext))
    return <i className="bi bi-file-earmark-excel-fill me-2" aria-hidden />;
  if (ext && [".mp4", ".mkv", ".mov", ".avi"].includes(ext))
    return <i className="bi bi-file-earmark-play-fill me-2" aria-hidden />;
  return <i className="bi bi-file-earmark-fill me-2" aria-hidden />;
};

const OneDriveFileManager = () => {
  const token = useMemo(
    () => localStorage.getItem("access_token") || sessionStorage.getItem("access_token"),
    []
  );

  // Tree state (lazy loaded nodes by parent_id)
  const [treeCache, setTreeCache] = useState(new Map());
  const [expanded, setExpanded] = useState(new Set());

  // Current folder
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [currentPath, setCurrentPath] = useState([]);
  const [items, setItems] = useState([]);

  // UI state
  const [view, setView] = useState("grid");
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState([]);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveTo, setMoveTo] = useState({ id: null, path: "" });

  // Upload
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // ===== API helpers =====
  const apiClient = api.create({
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  const fetchChildren = async (parent_id) => {
    const pid = parent_id === undefined ? null : parent_id;
    const url = `${FILES_BASE}/tree-view${
      pid === null ? "" : `?parent_id=${pid}`
    }`;
    const { data } = await api.get(url);
    return data;
  };

  const refreshFolder = async (
    parent_id,
    pushToPath = false,
    folderName = "Root"
  ) => {
    setLoading(true);
    try {
      const data = await fetchChildren(parent_id ?? null);
      setItems(data);
      setCurrentFolderId(parent_id ?? null);
      setTreeCache((prev) => new Map(prev).set(parent_id ?? null, data));

      if (pushToPath) {
        if (parent_id === null) setCurrentPath([{ id: null, name: "Root" }]);
        else
          setCurrentPath((prev) => [
            ...prev,
            { id: parent_id, name: folderName },
          ]);
      } else if (parent_id === null && currentPath.length === 0) {
        setCurrentPath([{ id: null, name: "Root" }]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshFolder(null, true, "Root");
  }, []);

  const toggleExpand = async (folder) => {
    const isOpen = expanded.has(folder.user_files_id);
    const next = new Set(expanded);
    if (isOpen) {
      next.delete(folder.user_files_id);
      setExpanded(next);
      return;
    }
    next.add(folder.user_files_id);
    setExpanded(next);

    if (!treeCache.has(folder.user_files_id)) {
      const children = await fetchChildren(folder.user_files_id);
      setTreeCache((prev) => new Map(prev).set(folder.user_files_id, children));
    }
  };

  const openFolder = async (folder) => {
    await refreshFolder(folder.user_files_id, true, folder.user_files_name);
  };

  const goToBreadcrumb = async (crumb, index) => {
    const newTrail = currentPath.slice(0, index + 1);
    setCurrentPath(newTrail);
    await refreshFolder(crumb.id, false);
  };

  const createFolder = async () => {
    try {
      if (!newFolderName.trim()) return;

      const parent_id = currentFolderId || null; // ✅ If inside a subfolder, send ID; otherwise null

      await api.post(`${FILES_BASE}/create-folder`, {
        folder_name: newFolderName.trim(),
        parent_id, // ✅ Send only parent_id, NOT parent_path
      });

      // ✅ Reset modal & refresh folders
      setShowCreateModal(false);
      setNewFolderName("");
      await refreshFolder(currentFolderId);
    } catch (error) {
      console.error("Error creating folder:", error);
      alert(error.response?.data?.message || "Failed to create folder");
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    const parent_id = currentFolderId;
    const parent_path = "uploads";

    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      form.append("parent_id", parent_id ?? "");
      form.append("parent_path", parent_path);

      await api.post(`${FILES_BASE}/upload`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    }
    await refreshFolder(currentFolderId);
  };

  const doRename = async () => {
    if (!renameTarget || !renameText.trim()) return;
    await api.put(`${FILES_BASE}/rename`, {
      item_id: renameTarget.user_files_id,
      new_name: renameText.trim(),
    });
    setShowRenameModal(false);
    setRenameTarget(null);
    setRenameText("");
    await refreshFolder(currentFolderId);
  };

  const doDelete = async (item) => {
    if (!window.confirm(`Delete "${item.user_files_name}"?`)) return;
    await api.delete(`${FILES_BASE}/delete`, {
      data: { item_id: item.user_files_id },
    });
    await refreshFolder(currentFolderId);
  };

  const doMove = async () => {
    if (!moveTarget || !moveTo.id) {
      alert("Please select a destination folder!");
      return;
    }

    try {
      await api.put(`${FILES_BASE}/move`, {
        item_id: moveTarget.user_files_id,
        target_path: moveTo.path, // ✅ Correct DB path
        target_parent_id: moveTo.id, // ✅ Destination folder ID
      });

      // Close modal and refresh
      setShowMoveModal(false);
      setMoveTarget(null);
      setMoveTo({ id: null, path: "" });
      await refreshFolder(currentFolderId);
    } catch (error) {
      console.error("Move Error:", error);
      alert("Failed to move item!");
    }
  };

  const onItemClick = (item, e) => {
    if (e?.ctrlKey || e?.metaKey) {
      setSelection((prev) =>
        prev.includes(item.user_files_id)
          ? prev.filter((id) => id !== item.user_files_id)
          : [...prev, item.user_files_id]
      );
      return;
    }
    if (item.item_type === "folder") openFolder(item);
  };

  const onContextMenu = (item, e) => {
    e.preventDefault();
    setSelection([item.user_files_id]);
    setRenameTarget(item);
    setRenameText(item.user_files_name);
    setShowRenameModal(true);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    await handleUpload(files);
  };

  const renderTree = (parent_id = null, level = 0) => {
    const nodes =
      treeCache.get(parent_id ?? null) || (parent_id === null ? items : []);

    return (
      <ul className="list-unstyled ms-2 slide-in-left">
        {nodes
          .filter((n) => n.item_type === "folder")
          .map((folder) => {
            const isOpen = expanded.has(folder.user_files_id);
            const isSelected = moveTo.id === folder.user_files_id;

            return (
              <li
                key={`tree-${folder.user_files_id}`}
                className={`folder-node-item ${isSelected ? "rounded" : ""}`}
                style={{ cursor: "pointer" }}
              >
                <div
                  className="d-flex align-items-center py-1 folder-node"
                  title="Click to select, double-click to open"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMoveTo({
                      id: folder.user_files_id,
                      path: folder.user_files_path, // ✅ Set correct absolute path
                    });
                  }}
                  onDoubleClick={() => openFolder(folder)}
                >
                  <i
                    className={`me-1 bi ${
                      isOpen ? "bi-caret-down-fill" : "bi-caret-right-fill"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(folder);
                    }}
                  />
                  <ItemIcon type="folder" />
                  <span className="text-truncate ms-1">
                    {folder.user_files_name}
                  </span>
                </div>

                {isOpen && renderTree(folder.user_files_id, level + 1)}
              </li>
            );
          })}
      </ul>
    );
  };

  const GridItem = ({ item }) => {
    const selected = selection.includes(item.user_files_id);
    return (
      <div
        className={`col-6 col-sm-4 col-md-3 col-lg-2 mb-3 fade-in-up`}
        onClick={(e) => onItemClick(item, e)}
        onContextMenu={(e) => onContextMenu(item, e)}
        role="button"
        style={{ animationDelay: `${Math.random() * 0.3}s` }}
      >
        <div className={`card h-100 ${selected ? "border-primary" : ""}`}>
          <div className="card-body d-flex flex-column align-items-center text-center">
            <div className="display-6">
              <ItemIcon type={item.item_type} ext={item.file_extension} />
            </div>
            <div
              className="small mt-2 text-truncate w-100"
              title={item.user_files_name}
            >
              {item.user_files_name}
            </div>
            {item.item_type === "file" && (
              <div className="text-muted small mt-1">
                {formatBytes(item.file_size)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ListItem = ({ item }) => {
    const selected = selection.includes(item.user_files_id);
    return (
      <tr
        className={selected ? "table-primary" : ""}
        onClick={(e) => onItemClick(item, e)}
        onContextMenu={(e) => onContextMenu(item, e)}
        role="button"
      >
        <td className="w-auto">
          <ItemIcon type={item.item_type} ext={item.file_extension} />
        </td>
        <td className="text-truncate"
          title={item.user_files_name}
          style={{
            maxWidth: "190px",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}>
          {item.user_files_name}
        </td>
        <td>
          {item.item_type === "file" ? item.file_extension || "" : "Folder"}
        </td>
        <td>{item.item_type === "file" ? formatBytes(item.file_size) : "—"}</td>
        <td
           title={item.user_files_path}
          style={{
            maxWidth: "190px",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
          className="text-muted small text-truncate"
        >
          {item.user_files_path}
        </td>
        <td className="text-end">
          <button
            className="btn btn-sm btn-outline-secondary me-2"
            onClick={(e) => {
              e.stopPropagation();
              setRenameTarget(item);
              setRenameText(item.user_files_name);
              setShowRenameModal(true);
            }}
          >
            <i className="bi bi-pencil-square me-1"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-secondary me-2"
            onClick={(e) => {
              e.stopPropagation();
              setMoveTarget(item);
              setMoveTo({ id: currentFolderId, path: "uploads" });
              setShowMoveModal(true);
            }}
          >
            <i className="bi bi-arrow-right-square me-1"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={(e) => {
              e.stopPropagation();
              doDelete(item);
            }}
          >
            <i className="bi bi-trash me-1"></i>
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="container-fluid py-3">
      {/* Toolbar */}
      <div className="d-flex align-items-center justify-content-between mb-3 toolbar">
        <div className="d-flex align-items-center gap-3">
          <button
            className="custom-btn"
            onClick={() => setShowCreateModal(true)}
          >
            <i className="bi bi-folder-plus me-2" /> New Folder
          </button>
          <button
            className="btn btn-outline-dark"
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="bi bi-upload me-2" /> Upload Files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => handleUpload(e.target.files)}
          />
        </div>
        <div className="d-flex align-items-center gap-2 view-toggle">
          <button
            className={`btn ${
              view === "grid" ? "btn-secondary" : "btn-outline-secondary"
            }`}
            onClick={() => setView("grid")}
            title="Grid view"
          >
            <i className="bi bi-grid" />
          </button>
          <button
            className={`btn ${
              view === "list" ? "btn-secondary" : "btn-outline-secondary"
            }`}
            onClick={() => setView("list")}
            title="List view"
          >
            <i className="bi bi-list" />
          </button>
        </div>
      </div>

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          {currentPath.map((crumb, idx) => (
            <li
              key={`bc-${idx}`}
              className={`breadcrumb-item ${
                idx === currentPath.length - 1 ? "active" : ""
              }`}
              aria-current={idx === currentPath.length - 1 ? "page" : undefined}
            >
              {idx === currentPath.length - 1 ? (
                <span>
                  <i className="bi bi-folder me-2"></i>
                  {crumb.name}
                </span>
              ) : (
                <a style={{textDecoration:"none",color:"#000"}}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    goToBreadcrumb(crumb, idx);
                  }}
                >
                  <i className="bi bi-folder me-2"></i>
                  {crumb.name}
                </a>
              )}
            </li>
          ))}
        </ol>
      </nav>

      {/* Layout: Tree + Main */}
      <div className="row" style={{ minHeight: 520 }}>
        <div className="col-12 col-md-3 col-lg-2">
          <div className="tree-sidebar">
            <div className="d-flex align-items-center mb-3">
              <strong className="me-2">
                <i className="bi bi-folder-symlink me-2"></i>Folders
              </strong>
              <button
                className="btn btn-sm btn-outline-secondary ms-auto"
                onClick={() => refreshFolder(currentFolderId)}
                title="Refresh"
              >
                <i className="bi bi-arrow-clockwise" />
              </button>
            </div>
            <div className="tree-view small">
              <div className="mb-2 text-muted">
                <i className="bi bi-house-door me-2"></i>Root
              </div>
              {renderTree(null)}
            </div>
          </div>
        </div>

        <div className="col-12 col-md-9 col-lg-10">
          <div className="main-content">
            {/* Drop zone */}
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              className={`drop-zone rounded p-3 mb-3 ${
                isDragging ? "dragging border-3 border-primary" : "border-1"
              }`}
            >
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-2 text-muted h5">
                    Loading your files...
                  </div>
                </div>
              ) : items.length === 0 ? (
                <div className="text-center text-muted py-5 empty-state">
                  <i className="bi bi-cloud-arrow-up display-1 d-block mb-3" />
                  <div className="h4 mb-2">Drop files here to upload</div>
                  <div className="text-muted">
                    Or use the Upload Files button above
                  </div>
                </div>
              ) : view === "grid" ? (
                <div className="row">
                  {items.map((item) => (
                    <GridItem key={item.user_files_id} item={item} />
                  ))}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>
                          <i className="bi bi-card-list"></i>
                        </th>
                        <th>
                          <i className="bi bi-file-earmark me-2"></i>Name
                        </th>
                        <th>
                          <i className="bi bi-tag me-2"></i>Type
                        </th>
                        <th>
                          <i className="bi bi-hdd me-2"></i>Size
                        </th>
                        <th>
                          <i className="bi bi-folder me-2"></i>Path
                        </th>
                        <th className="text-end">
                          <i className="bi bi-gear me-2"></i>Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <ListItem key={item.user_files_id} item={item} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Folder Modal */}
      <div
        className={`modal ${showCreateModal ? "d-block show" : ""}`}
        tabIndex="-1"
        style={{
          background: showCreateModal ? "rgba(0,0,0,.6)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered w-50">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-folder-plus me-2"></i>Create New Folder
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowCreateModal(false)}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Folder Name</label>
                <input
                  className="form-control"
                  placeholder="Enter folder name..."
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && createFolder()}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCreateModal(false)}
              >
                <i className="bi bi-x-lg me-2"></i>Cancel
              </button>
              <button
                className="custom-btn"
                onClick={createFolder}
                disabled={!newFolderName.trim()}
              >
                <i className="bi bi-check-lg me-2"></i>Create Folder
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Rename Modal */}
      <div
        className={`modal ${showRenameModal ? "d-block show" : ""}`}
        tabIndex="-1"
        style={{
          background: showRenameModal ? "rgba(0,0,0,.6)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered w-50">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-pencil-square me-2"></i>Rename Item
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowRenameModal(false)}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">New Name</label>
                <input
                  className="form-control"
                  value={renameText}
                  onChange={(e) => setRenameText(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && doRename()}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowRenameModal(false)}
              >
                <i className="bi bi-x-lg me-2"></i>Cancel
              </button>
              <button
                className="custom-btn"
                onClick={doRename}
                disabled={!renameText.trim()}
              >
                <i className="bi bi-check-lg me-2"></i>Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Move Modal */}
      {/* Move Modal */}
      <div
        className={`modal ${showMoveModal ? "d-block show" : ""}`}
        tabIndex="-1"
        style={{ background: showMoveModal ? "rgba(0,0,0,.6)" : "transparent" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered w-50">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-arrow-right-square me-2"></i>
                Move Item
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowMoveModal(false)}
              ></button>
            </div>

            {/* Body */}
            <div className="modal-body">
              <div className="row">
                {/* Left Section - Destination Tree */}
                <div className="col-12 col-md-6 border-end">
                  <div className="mb-3">
                    <h6 className="text-muted">
                      <i className="bi bi-folder-symlink me-2"></i>
                      Select Destination
                    </h6>
                  </div>
                  <div
                    className="dest-tree"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    {renderTree(null)}
                  </div>
                </div>

                {/* Right Section - Selected Destination */}
                <div className="col-12 col-md-6">
                  <div className="mb-3">
                    <h6 className="text-muted">
                      <i className="bi bi-geo-alt me-2"></i>
                      Selected Destination
                    </h6>
                  </div>

                  {/* Selected Folder */}
                  <div
                    className="border rounded p-3 mb-3"
                    style={{ backgroundColor: "rgba(0, 120, 212, 0.05)" }}
                  >
                    <div className="d-flex align-items-center">
                      <ItemIcon type="folder" />
                      <span className="text-truncate">
                        {moveTo.path || "No folder selected"}
                      </span>
                    </div>
                  </div>

                  {/* Quick Options */}
                  <div className="d-grid">
                    <button
                      className="btn btn-outline-secondary mb-2"
                      onClick={() =>
                        setMoveTo({
                          id: currentFolderId,
                          path: "Current Folder",
                        })
                      }
                    >
                      <i className="bi bi-folder me-2"></i>
                      Use Current Folder
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setMoveTo({ id: null, path: "Root" })}
                    >
                      <i className="bi bi-house-door me-2"></i>
                      Move to Root
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowMoveModal(false)}
              >
                <i className="bi bi-x-lg me-2"></i>
                Cancel
              </button>
              <button
                className="custom-btn"
                onClick={doMove}
                disabled={!moveTo.id && moveTo.path !== "Root"}
              >
                <i className="bi bi-arrow-right me-2"></i>
                Move Here
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OneDriveFileManager;
